import React, { useState, useMemo, useEffect } from 'react';
import { 
  Search, Plus, Filter, FileText, Upload, Copy, Edit2, Trash2, Library, 
  Layers, GraduationCap, ChevronRight, CheckCircle2, User, Crown, 
  AlertCircle, Loader2, BookOpen, FolderOpen, Globe 
} from 'lucide-react';
import { Button } from '../ui/Button';
import { useAuth } from '../../AuthProvider';
import { QuestionSetCard } from './QuestionSetCard';
import { CreateQuestionSetModal } from './CreateQuestionSetModal';
import { ConfirmDeleteModal } from './ConfirmDeleteModal';
import { QuotaExceededModal } from './QuotaExceededModal';
import { useMyQuestionSets, usePublicQuestionSets, useCloneQuestionSet, useDeleteQuestionSet } from './hooks';
import { QUESTION_SUBJECTS, QUESTION_GRADES } from '../../types';

// Types remain the same
interface QuestionSet {
  id: string;
  _id?: string;
  name?: string;
  title?: string;
  description?: string;
  subject?: string;
  grade?: string;
  totalQuestions?: number;
  questionCount?: number;
  isPublic: boolean;
  createdAt?: string;
  updatedAt?: string;
  userId?: string;
  userEmail?: string;
  userName?: string;
  userAvatar?: string;
  cloneCount?: number;
  viewCount?: number;
  clonedFromId?: string;
  user?: any;
}

interface QuestionSetListProps {
  onOpenSet: (setId: string) => void;
  onOpenTopUp?: () => void;
}

export const QuestionSetList: React.FC<QuestionSetListProps> = ({ onOpenSet, onOpenTopUp }) => {
  const { user: authUser } = useAuth();
  
  // States
  const [activeSubTab, setActiveSubTab] = useState<'mine' | 'public'>('mine');
  const [notification, setNotification] = useState<string | null>(null);
  
  // Filters & Pagination
  const [page, setPage] = useState<number>(1);
  const [limit, setLimit] = useState<number>(9);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [debouncedSearch, setDebouncedSearch] = useState<string>('');
  const [selectedSubject, setSelectedSubject] = useState<string>('all');
  const [selectedGrade, setSelectedGrade] = useState<string>('all');
  const [sortBy, setSortBy] = useState<string>('newest');

  // Debounce search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
      setPage(1); // Reset page on new search
    }, 500);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Query Params
  const queryParams = useMemo(() => {
    const params: any = { page, limit };
    if (debouncedSearch.trim()) params.search = debouncedSearch.trim();
    if (selectedSubject !== 'all') params.subject = selectedSubject;
    if (selectedGrade !== 'all') params.grade = selectedGrade;
    if (activeSubTab === 'public' && sortBy !== 'newest') params.sortBy = sortBy;
    return params;
  }, [page, limit, debouncedSearch, selectedSubject, selectedGrade, activeSubTab, sortBy]);

  // Queries
  const mySetsQuery = useMyQuestionSets(activeSubTab === 'mine' ? queryParams : {});
  const publicSetsQuery = usePublicQuestionSets(activeSubTab === 'public' ? queryParams : {});
  
  const currentQuery = activeSubTab === 'mine' ? mySetsQuery : publicSetsQuery;
  const { data: queryData, isLoading: loading, error: queryError, isFetching } = currentQuery;
  
  const sets = queryData?.sets || [];
  const total = queryData?.total || 0;
  const quota = queryData?.quota || null;
  const error = queryError?.message || null;

  // Mutations
  const cloneMutation = useCloneQuestionSet();
  const deleteMutation = useDeleteQuestionSet();

  // Modals & action states
  const [isCreateModalOpen, setIsCreateModalOpen] = useState<boolean>(false);
  const [editingSet, setEditingSet] = useState<QuestionSet | null>(null);
  const [deletingSet, setDeletingSet] = useState<QuestionSet | null>(null);

  // 403 / Quota Exceeded Modal State
  const [quotaExceededModal, setQuotaExceededModal] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    type: 'set' | 'question' | 'general';
  }>({
    isOpen: false,
    title: 'Đã đạt giới hạn số lượng bộ câu hỏi',
    message: '',
    type: 'set'
  });

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
  };

  const handleCloneSet = async (set: QuestionSet) => {
    try {
      await cloneMutation.mutateAsync(set.id);
      setNotification(`Nhân bản thành công bộ câu hỏi "${set.name || set.title}" về kho của bạn!`);
      setTimeout(() => setNotification(null), 4000);
      setActiveSubTab('mine');
    } catch (err: any) {
      if (err.message.includes('giới hạn')) {
        setQuotaExceededModal({
          isOpen: true,
          title: 'Đã đạt giới hạn số lượng bộ câu hỏi',
          message: err.message || 'Tài khoản của Thầy/Cô đã đạt giới hạn tối đa số lượng bộ câu hỏi cho phép. Vui lòng nâng cấp VIP/SVIP để nhân bản thêm!',
          type: 'set'
        });
      }
    }
  };

  const confirmDeleteSet = async () => {
    if (!deletingSet) return;
    try {
      await deleteMutation.mutateAsync(deletingSet.id);
      setNotification(`Đã xóa thành công bộ câu hỏi "${deletingSet.name || deletingSet.title}".`);
      setTimeout(() => setNotification(null), 4000);
      setDeletingSet(null);
    } catch (err: any) {
      console.error(err);
    }
  };

  const handleEditSet = (set: QuestionSet) => {
    setEditingSet(set);
    setIsCreateModalOpen(true);
  };

  const handleOpenDeleteConfirm = (set: QuestionSet) => {
    setDeletingSet(set);
  };

  // Logic related to UI (ProgressBar for quotas, etc.)
  const authVipTierNum = typeof authUser?.vipTier === 'number' 
    ? authUser.vipTier 
    : (authUser?.vipTier === 'SVIP' || (authUser?.vipTier as any) === 2 ? 2 : authUser?.vipTier === 'VIP' || (authUser?.vipTier as any) === 1 ? 1 : Number(authUser?.vipTier) || 0);

  const isAdmin = authUser?.role === 'admin' || (Array.isArray(authUser?.roles) && authUser.roles.includes('admin'));
  const isSVIP = isAdmin || authVipTierNum >= 2 || authUser?.role === 'SVIP' || (Array.isArray(authUser?.roles) && authUser.roles.includes('SVIP')) || quota?.userTier === 'SVIP';
  const isVIP = !isSVIP && (authVipTierNum === 1 || authUser?.role === 'VIP' || (Array.isArray(authUser?.roles) && authUser.roles.includes('VIP')) || quota?.userTier === 'VIP');

  let effectiveTier = 'Thường';
  let maxSets = 10;

  if (isAdmin) {
    effectiveTier = 'Admin';
    maxSets = 9999;
  } else if (isSVIP) {
    effectiveTier = 'SVIP';
    maxSets = 40;
  } else if (isVIP) {
    effectiveTier = 'VIP';
    maxSets = 20;
  } else if (quota?.maxSets) {
    maxSets = quota.maxSets;
    effectiveTier = quota.userTier || 'Thường';
  }

  const currentSets = typeof quota?.currentSets === 'number' 
    ? quota.currentSets 
    : (activeSubTab === 'mine' && mySetsQuery.data?.total !== undefined ? mySetsQuery.data.total : (mySetsQuery.data?.sets?.length || 0));

  const usagePercent = maxSets >= 9999 ? 0 : Math.min(100, Math.round((currentSets / maxSets) * 100)) || 0;
  let progressColor = 'bg-[#236539]';
  if (usagePercent >= 90) progressColor = 'bg-red-500';
  else if (usagePercent >= 70) progressColor = 'bg-amber-500';

  return (
    <div className="space-y-6">
      {/* Banner / Header Area */}
      <div className="bg-gradient-to-r from-[#236539] to-[#174626] text-white rounded-2xl p-5 sm:p-6 shadow-md relative overflow-hidden">
        {/* Background decorative pattern */}
        <div className="absolute right-0 top-0 bottom-0 opacity-10 pointer-events-none flex items-center pr-6">
          <BookOpen className="w-48 h-48 text-white" />
        </div>

        <div className="relative z-10 max-w-2xl space-y-2">
          <h2 className="text-xl sm:text-2xl font-black tracking-tight">
            Ngân hàng câu hỏi trắc nghiệm ôn tập
          </h2>

          <p className="text-xs sm:text-sm text-emerald-100/90 leading-relaxed">
            Nơi lưu trữ các bộ đề câu hỏi trắc nghiệm ôn tập của giáo viên. Hỗ trợ tạo câu hỏi bằng AI, nhập từ file và nhập thủ công.
          </p>
        </div>
      </div>

      {/* 2 Main Tabs: Kho của tôi vs Thư viện cộng đồng */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-slate-200 pb-3">
        <div className="flex items-center space-x-2 bg-slate-100 p-1 rounded-2xl w-fit">
          <button
            type="button"
            onClick={() => {
              setActiveSubTab('mine');
              setPage(1);
            }}
            className={`px-4 py-2 rounded-xl text-xs sm:text-sm font-bold transition-all flex items-center space-x-2 cursor-pointer ${
              activeSubTab === 'mine'
                ? 'bg-white text-[#236539] shadow-xs'
                : 'text-slate-600 hover:text-slate-900'
            }`}
          >
            <FolderOpen className="w-4 h-4" />
            <span>Kho của tôi</span>
          </button>

          <button
            type="button"
            onClick={() => {
              setActiveSubTab('public');
              setPage(1);
            }}
            className={`px-4 py-2 rounded-xl text-xs sm:text-sm font-bold transition-all flex items-center space-x-2 cursor-pointer ${
              activeSubTab === 'public'
                ? 'bg-white text-emerald-700 shadow-xs'
                : 'text-slate-600 hover:text-slate-900'
            }`}
          >
            <Globe className="w-4 h-4" />
            <span>Thư viện cộng đồng</span>
          </button>
        </div>

        {/* Action button & Storage Quota Progress */}
        <div className="flex flex-wrap items-center gap-3">
          {/* Nút Tạo bộ câu hỏi mới */}
          <button
            type="button"
            onClick={() => {
              setEditingSet(null);
              setIsCreateModalOpen(true);
            }}
            className="px-4 py-2 bg-[#236539] hover:bg-[#1a4e2b] text-white font-bold text-xs sm:text-sm rounded-xl shadow-md transition-all hover:shadow-lg active:scale-95 flex items-center space-x-1.5 cursor-pointer ring-2 ring-[#236539]/20"
          >
            <Plus className="w-4 h-4 text-white" />
            <span>Tạo bộ câu hỏi mới</span>
          </button>

          {activeSubTab === 'mine' && (
            <div className="bg-white border border-slate-200 rounded-2xl px-4 py-2 shadow-2xs flex items-center space-x-3 text-xs">
              <div className="space-y-1 min-w-[140px]">
                <div className="flex justify-between items-center font-bold">
                  <span className="text-slate-700">Bộ câu hỏi: {currentSets}/{maxSets >= 9999 ? '∞' : maxSets}</span>
                  <span className={`text-[11px] font-black uppercase px-1.5 py-0.5 rounded ${
                    effectiveTier === 'SVIP'
                      ? 'bg-rose-100 text-rose-700'
                      : effectiveTier === 'VIP'
                      ? 'bg-amber-100 text-amber-700'
                      : effectiveTier === 'Admin'
                      ? 'bg-purple-100 text-purple-700'
                      : 'bg-slate-100 text-slate-600'
                  }`}>
                    {effectiveTier}
                  </span>
                </div>
                <div className="w-full bg-slate-100 rounded-full h-2 overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all ${
                      usagePercent >= 90
                        ? 'bg-rose-500'
                        : usagePercent >= 70
                        ? 'bg-amber-500'
                        : 'bg-[#236539]'
                    }`}
                    style={{ width: `${maxSets >= 9999 ? 5 : usagePercent}%` }}
                  />
                </div>
              </div>
              {effectiveTier !== 'SVIP' && effectiveTier !== 'Admin' && onOpenTopUp && (
                <button
                  type="button"
                  onClick={onOpenTopUp}
                  className="px-2.5 py-1 bg-amber-50 hover:bg-amber-100 text-amber-900 border-2 border-amber-400 font-bold text-[11px] rounded-lg shadow-2xs hover:shadow-xs transition-all shrink-0 cursor-pointer flex items-center space-x-1"
                >
                  <Crown className="w-3 h-3 text-amber-600 shrink-0" />
                  <span>{effectiveTier === 'VIP' ? 'Nâng cấp SVIP' : 'Tăng hạn mức'}</span>
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Notifications */}
      {notification && (
        <div className="p-3.5 bg-emerald-50 border border-emerald-200 text-emerald-800 text-xs font-semibold rounded-xl flex items-center space-x-2 animate-in fade-in">
          <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
          <span>{notification}</span>
        </div>
      )}

      {error && (
        <div className="p-3.5 bg-rose-50 border border-rose-200 text-rose-700 text-xs font-semibold rounded-xl flex items-center justify-between animate-in fade-in">
          <div className="flex items-center space-x-2">
            <AlertCircle className="w-4 h-4 text-rose-600 shrink-0" />
            <span>{error}</span>
          </div>
          <button
            type="button"
            onClick={() => currentQuery.refetch()}
            className="text-xs font-bold underline hover:text-rose-900"
          >
            Thử lại
          </button>
        </div>
      )}

      {/* Filter and Search Bar */}
      <div className="bg-white rounded-2xl border border-slate-200 p-4 shadow-2xs space-y-3">
        <form onSubmit={handleSearchSubmit} className="grid grid-cols-1 sm:grid-cols-12 gap-3">
          {/* Search Input */}
          <div className={activeSubTab === 'public' ? "col-span-1 sm:col-span-12 md:col-span-4 lg:col-span-5 relative" : "col-span-1 sm:col-span-6 relative"}>
            <Search className="w-4 h-4 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Tìm kiếm theo tên bộ câu hỏi, môn học, tác giả..."
              className="w-full bg-slate-50 border border-slate-200 rounded-xl py-2 pl-9 pr-3 text-xs sm:text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#236539]/20 focus:border-[#236539] focus:bg-white"
            />
          </div>

          {/* Subject Select */}
          <div className={activeSubTab === 'public' ? "col-span-1 sm:col-span-4 md:col-span-3 lg:col-span-2" : "col-span-1 sm:col-span-3"}>
            <select
              value={selectedSubject}
              onChange={(e) => {
                setSelectedSubject(e.target.value);
                setPage(1);
              }}
              className="w-full bg-slate-50 border border-slate-200 rounded-xl py-2 px-3 text-xs sm:text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-[#236539]/20 focus:border-[#236539]"
            >
              <option value="all">Tất cả môn học</option>
              {QUESTION_SUBJECTS.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </div>

          {/* Grade Select */}
          <div className={activeSubTab === 'public' ? "col-span-1 sm:col-span-4 md:col-span-2 lg:col-span-2" : "col-span-1 sm:col-span-3"}>
            <select
              value={selectedGrade}
              onChange={(e) => {
                setSelectedGrade(e.target.value);
                setPage(1);
              }}
              className="w-full bg-slate-50 border border-slate-200 rounded-xl py-2 px-3 text-xs sm:text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-[#236539]/20 focus:border-[#236539]"
            >
              <option value="all">Tất cả khối lớp</option>
              {QUESTION_GRADES.map((g) => (
                <option key={g} value={g}>
                  {isNaN(Number(g)) ? g : `Lớp ${g}`}
                </option>
              ))}
            </select>
          </div>

          {/* Sort Select (Only for Public Library) */}
          {activeSubTab === 'public' && (
            <div className="col-span-1 sm:col-span-4 md:col-span-3 lg:col-span-3">
              <select
                value={sortBy}
                onChange={(e) => {
                  setSortBy(e.target.value);
                  setPage(1);
                }}
                className="w-full bg-slate-50 border border-slate-200 rounded-xl py-2 px-3 text-xs sm:text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-[#236539]/20 focus:border-[#236539]"
              >
                <option value="newest">Sắp xếp: Mới nhất</option>
                <option value="clones">Nhân bản nhiều nhất</option>
              </select>
            </div>
          )}
        </form>
      </div>

      {/* Grid of Question Sets */}
      {loading ? (
        <div className="p-16 flex flex-col items-center justify-center space-y-3 bg-white rounded-3xl border border-slate-200 shadow-2xs">
          <Loader2 className="w-8 h-8 text-[#236539] animate-spin" />
          <p className="text-xs font-semibold text-slate-500">Đang tải danh sách bộ câu hỏi...</p>
        </div>
      ) : sets.length === 0 ? (
        <div className="bg-white rounded-3xl border border-dashed border-slate-300 p-12 text-center space-y-4">
          <div className="w-14 h-14 rounded-2xl bg-emerald-50 text-[#236539] flex items-center justify-center mx-auto">
            <Layers className="w-7 h-7" />
          </div>
          <div>
            <h4 className="text-base font-bold text-slate-800">
              {activeSubTab === 'mine' ? 'Bạn chưa có bộ câu hỏi nào' : 'Chưa có bộ câu hỏi cộng đồng phù hợp'}
            </h4>
            <p className="text-xs text-slate-500 mt-1 max-w-md mx-auto">
              {activeSubTab === 'mine'
                ? 'Hãy bấm "Tạo bộ câu hỏi mới" để bắt đầu xây dựng kho trắc nghiệm của riêng bạn!'
                : 'Thử thay đổi từ khóa tìm kiếm hoặc lọc theo môn học/lớp khác.'}
            </p>
          </div>
          {activeSubTab === 'mine' && (
            <Button
              onClick={() => {
                setEditingSet(null);
                setIsCreateModalOpen(true);
              }}
              className="bg-[#236539] hover:bg-[#1b4f2c] text-white shadow-md text-xs font-bold"
            >
              <Plus className="w-4 h-4 mr-1" />
              Tạo bộ câu hỏi đầu tiên
            </Button>
          )}
        </div>
      ) : (
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {sets.map((set) => {
              const currentUserId = authUser?.id || authUser?._id || authUser?.uid || '';
              const currentUserEmail = authUser?.email || '';
              const isActualOwner = activeSubTab === 'mine' || Boolean(
                (currentUserId && set.userId && String(set.userId) === String(currentUserId)) ||
                (currentUserEmail && set.userId && String(set.userId).toLowerCase() === currentUserEmail.toLowerCase())
              );
              const isAdmin = authUser?.role === 'admin' || (Array.isArray(authUser?.roles) && authUser.roles.includes('admin'));
              const canManageSet = isActualOwner || isAdmin;

              return (
                <QuestionSetCard
                  key={set.id}
                  set={set}
                  isMine={canManageSet}
                  onOpen={(s) => onOpenSet(s.id)}
                  onEdit={canManageSet ? handleEditSet : undefined}
                  onDelete={canManageSet ? handleOpenDeleteConfirm : undefined}
                  onClone={handleCloneSet}
                  cloning={cloneMutation.isPending && cloneMutation.variables === set.id}
                />
              );
            })}
          </div>

          {/* Pagination Controls */}
          {total > limit && (
            <div className="flex justify-center items-center space-x-2 pt-4">
              <button
                type="button"
                disabled={page === 1}
                onClick={() => setPage(p => Math.max(1, p - 1))}
                className="px-3.5 py-1.5 text-xs font-semibold text-slate-700 bg-white border border-slate-200 rounded-lg shadow-2xs hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
              >
                Trước
              </button>
              
              <div className="flex items-center space-x-1 overflow-x-auto max-w-xs sm:max-w-md px-2 scrollbar-hide">
                {Array.from({ length: Math.ceil(total / limit) }, (_, i) => i + 1).map(p => (
                  <button
                    key={p}
                    type="button"
                    onClick={() => setPage(p)}
                    className={`w-8 h-8 shrink-0 flex items-center justify-center text-xs font-bold rounded-lg transition-all ${
                      page === p 
                        ? 'bg-[#236539] text-white shadow-md' 
                        : 'text-slate-600 bg-white border border-slate-200 hover:bg-slate-50 hover:text-slate-900 shadow-2xs'
                    }`}
                  >
                    {p}
                  </button>
                ))}
              </div>

              <button
                type="button"
                disabled={page >= Math.ceil(total / limit)}
                onClick={() => setPage(p => Math.min(Math.ceil(total / limit), p + 1))}
                className="px-3.5 py-1.5 text-xs font-semibold text-slate-700 bg-white border border-slate-200 rounded-lg shadow-2xs hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
              >
                Tiếp
              </button>
            </div>
          )}
        </div>
      )}

      {/* Create / Edit Modal for Question Sets */}
      <CreateQuestionSetModal
        isOpen={isCreateModalOpen}
        onClose={() => {
          setIsCreateModalOpen(false);
          setEditingSet(null);
        }}
        editingSet={editingSet}
        onQuotaExceeded={(msg) => {
          setQuotaExceededModal({
            isOpen: true,
            title: 'Đã đạt giới hạn số lượng bộ câu hỏi',
            message: msg || 'Tài khoản của Thầy/Cô đã đạt giới hạn tối đa số lượng bộ câu hỏi của gói hiện tại. Vui lòng nâng cấp VIP/SVIP để tạo thêm!',
            type: 'set'
          });
        }}
        onSuccess={(savedSet) => {
          const isEditMode = Boolean(editingSet);
          setIsCreateModalOpen(false);
          setEditingSet(null);

          if (!isEditMode && savedSet) {
            const newSetId = savedSet.id || savedSet._id;
            if (newSetId) {
              // UX: Điều hướng thẳng giáo viên vào trang chi tiết để thêm câu hỏi ngay lập tức
              onOpenSet(newSetId);
              return;
            }
          }

          setNotification(isEditMode ? 'Đã cập nhật thông tin bộ câu hỏi.' : 'Đã tạo bộ câu hỏi mới thành công!');
          currentQuery.refetch();
        }}
      />

      {/* Confirm Delete Modal for Question Set */}
      <ConfirmDeleteModal
        isOpen={Boolean(deletingSet)}
        onClose={() => setDeletingSet(null)}
        onConfirm={confirmDeleteSet}
        title="Xóa bộ câu hỏi"
        itemType="bộ câu hỏi"
        itemName={deletingSet?.name || deletingSet?.title}
        loading={deleteMutation.isPending}
      />

      {/* Quota Exceeded Modal */}
      <QuotaExceededModal
        isOpen={quotaExceededModal.isOpen}
        onClose={() => setQuotaExceededModal(prev => ({ ...prev, isOpen: false }))}
        onOpenTopUp={onOpenTopUp}
        title={quotaExceededModal.title}
        message={quotaExceededModal.message}
        type={quotaExceededModal.type}
        currentTier={effectiveTier}
      />
    </div>
  );
};
